/* eslint-disable react/destructuring-assignment */
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Checkbox,
  Empty,
  Form,
  Pagination,
  Popover,
  Spin,
  Modal,
  message,
  Dropdown,
  Space,
} from "antd";
import { capitalize, isEmpty } from "lodash";
import React, { useContext, useEffect, useRef, useState, useMemo } from "react";
import { LuUser } from "react-icons/lu";
import { UserContext } from "../../../../amplify/authenticator-provider";
import { GET_EMAILS } from "../../../../graphql/query";
import UserSelector from "../../../commonComponents/userSelector";
import { EmailCard } from "./EmailCard";
import DeleteIcon from "../../../../icons/js/DeleteIcon";
import { UPDATE_EMAIL_STATUS } from "../../../../graphql/mutation";
import CustomButton from "../../../commonComponents/button";
import { EMAIL_STATUS_ENUMS, EMAIL_TYPE_ENUMS } from "../../../../constant/EmailConstant";
import DebounceInput from "../../../commonComponents/DebounceInput";
import { checkAccessPermission } from "../../../../library/helpers";
import { AccessPermissionAction } from "../../../../library/constants";

export const UserFilter = props => {
  const [userList, setuserList] = useState(props.value || []);

  // When the "restrict to own data" permission pins a user, keep the picker in
  // sync with the locked value coming from the parent.
  useEffect(() => {
    if (props.disabled) {
      setuserList(props.value || []);
    }
  }, [props.disabled, props.value]);

  const close = () => props.employeeBtnRef.current.click();
  return (
    <Form layout="vertical" className="w-[300px] -m-3">
      <div className="flex-col-centered p-3">
        <span className="sm-600 text-grey-700">Sent By</span>
        <Form.Item label="Select employees">
          <UserSelector
            allowClear={!props.disabled}
            disabled={props.disabled}
            className="w-full"
            value={userList}
            handleUsersSelection={value => {
              setuserList(value);
            }}
            multiple
            extraQueryParams={{
              isAllLocationUsers: true,
            }}
          />
        </Form.Item>
      </div>
      <div className="flex-row-centered p-3 bg-grey-50 gap-2 justify-start">
        <Button
          type="primary"
          onClick={() => {
            close();
            props.applyUserFilter(userList);
          }}
        >
          Apply
        </Button>
        <Button onClick={close}>Cancel</Button>
      </div>
    </Form>
  );
};

export function EmailList(props) {
  const locationId = sessionStorage.getItem("currentLocationId");

  // @ts-ignore
  const { socket, user } = useContext(UserContext);
  // When set, the user may only view their own data: pin the logged-in user as
  // the "Sent By" filter and lock it. Mirrors the appointments/dashboard
  // handling of the same permission.
  const restrictRemoveDefaultFilter = checkAccessPermission(
    AccessPermissionAction.ACTIVITY_CENTER_RESTRICT_REMOVE_DEFAULT_FILTER
  );
  // @ts-ignore
  const [params, setparams] = useState({
    page: 1,
    pageSize: 20,
    search: "",
    // Remove emailStatus from params since we'll use the separate state variables
  });
  /**
   * @type {[import("./emailMockData").EmailsData[],import("react").Dispatch<import("react").SetStateAction<import("./emailMockData").EmailsData[]>>]}
   */
  // @ts-ignore
  const [emails, setemails] = useState([]);
  const [userList, setuserList] = useState([]);
  // New state for email filters
  const [selectedEmailTypes, setSelectedEmailTypes] = useState([]);
  const [selectedEmailStatuses, setSelectedEmailStatuses] = useState([]);

  const [selectedIds, setSelectedIds] = useState([]);
  const {
    data,
    loading: emailLoader,
    refetch: refetchEmailsList,
  } = useQuery(GET_EMAILS, {
    fetchPolicy: "network-only",
    variables: {
      ...params,
      userIds: userList,
      emailTypes: selectedEmailTypes.length > 0 ? selectedEmailTypes : undefined,
      emailStatus: selectedEmailStatuses.length > 0 ? selectedEmailStatuses : undefined,
    },
    onCompleted: res => {
      setemails(res?.getEmails?.data || []);
    },
  });

  // Refetch the GET_EMAILS list and sync local state. Both registered with
  // the parent (so the detail pane can trigger a list refresh) AND passed
  // directly to EmailCard, which needs to refresh the list after a resend.
  const handleRefetchEmails = async () => {
    try {
      const result = await refetchEmailsList();
      if (result?.data?.getEmails?.data) {
        setemails(result.data.getEmails.data);
      }
      return result;
    } catch (error) {
      console.error("Error refetching emails:", error);
    }
    return undefined;
  };

  useEffect(() => {
    if (refetchEmailsList) {
      props.refetchEmailListFn(handleRefetchEmails);
    }
  }, [refetchEmailsList]);

  useEffect(() => {
    socket &&
      socket.on("recieve_message", data => {
        if (data.user_id === user.id) {
          const message = data.lead_messages.length > 0 && data.lead_messages[0];
        }
      });
  }, []);

  useEffect(() => {
    props.setselectedEmail(emails[0]);
  }, [emails]);

  const employeeBtnRef = useRef(null);

  const handleCheckboxChange = (id, isChecked) => {
    setSelectedIds(prev =>
      isChecked ? [...prev, id] : prev.filter(selectedId => selectedId !== id)
    );
  };

  const handleSelectAll = e => {
    if (e.target.checked) {
      // Select all emails
      const allIds = emails.map(email => email.sesMessageId);
      setSelectedIds(allIds);
    } else {
      // Deselect all emails
      setSelectedIds([]);
    }
  };

  const [updateEmailStatus] = useMutation(UPDATE_EMAIL_STATUS);

  const handleDelete = async () => {
    try {
      const res = await updateEmailStatus({
        variables: {
          action: "DELETE",
          sesMessageIds: selectedIds,
        },
      });

      if (res.data.updateEmailStatus.statusCode === 200) {
        // Refetch the updated list from the server
        const latestEmail = await refetchEmailsList();
        setemails(latestEmail?.data?.getEmails?.data);
        setSelectedIds([]); // Clear selected IDs after moving to trash
        message.success(res.data.updateEmailStatus.message);
      } else {
        message.error(res.data.updateEmailStatus.message);
      }
    } catch (error) {
      message.error("Failed to delete emails. Please try again.");
    }
  };

  const handleMoveToTrash = () => {
    let modal = null; // Reference to the modal instance

    modal = Modal.confirm({
      title: "Are you sure?",
      content: "Are you sure, you want to delete all these emails? This will go into trash.",
      okText: "Yes",
      cancelText: "No",
      okType: "danger",
      okButtonProps: { loading: false }, // Initially set loading to false
      onOk: async () => {
        try {
          // Set loading to true manually
          modal.update({ okButtonProps: { loading: true } });

          await handleDelete();

          // Set loading to false after the operation
          modal.update({ okButtonProps: { loading: false } });
        } catch (error) {
          console.error("Error deleting emails:", error);
          throw error; // Keep the dialog open if an error occurs
        }
      },
    });
  };

  // Handler for email type checkbox changes
  const handleEmailTypeChange = (key, checked) => {
    setSelectedEmailTypes(prev => (checked ? [...prev, key] : prev.filter(item => item !== key)));
    setparams(prev => ({ ...prev, page: 1 })); // Reset to page 1
  };

  // Handler for email status checkbox changes
  const handleEmailStatusChange = (key, checked) => {
    setSelectedEmailStatuses(prev =>
      checked ? [...prev, key] : prev.filter(item => item !== key)
    );
    setparams(prev => ({ ...prev, page: 1 })); // Reset to page 1
  };

  // Handler for clearing all filters
  const clearAllFilters = () => {
    setSelectedEmailTypes([]);
    setSelectedEmailStatuses([]);
    setparams(prev => ({ ...prev, page: 1 })); // Reset to page 1
  };

  // Check if any filters are active
  const hasActiveFilters = selectedEmailTypes.length > 0 || selectedEmailStatuses.length > 0;

  useEffect(() => {
    // Reset all filters when locationId changes
    setparams({
      page: 1,
      pageSize: 20,
      search: "",
    });
    setuserList([]);
    setSelectedEmailTypes([]);
    setSelectedEmailStatuses([]);
    setSelectedIds([]);
  }, [locationId]);

  // Restricted users see only their own emails: pin the logged-in user into the
  // query's userIds and lock the "Sent By" filter (handled below via the
  // `disabled` prop). Re-runs on location change so the reset effect above
  // doesn't clear the pin.
  useEffect(() => {
    if (restrictRemoveDefaultFilter && user?.id) {
      setuserList([+user.id]);
    }
  }, [restrictRemoveDefaultFilter, user?.id, locationId]);

  // Create menu with checkboxes
  const menuWithCheckboxes = useMemo(
    () => ({
      items: [
        {
          key: "1",
          label: "Email Type",
          children: Object.entries(EMAIL_TYPE_ENUMS).map(([key, label]) => ({
            key,
            label: (
              <div onClick={e => e.stopPropagation()}>
                <Checkbox
                  checked={selectedEmailTypes.includes(key)}
                  onChange={e => handleEmailTypeChange(key, e.target.checked)}
                >
                  {capitalize(label)}
                </Checkbox>
              </div>
            ),
          })),
        },
        {
          key: "2",
          label: "Email Status",
          children: Object.entries(EMAIL_STATUS_ENUMS).map(([key, label]) => ({
            key,
            label: (
              <div onClick={e => e.stopPropagation()}>
                <Checkbox
                  checked={selectedEmailStatuses.includes(key)}
                  onChange={e => handleEmailStatusChange(key, e.target.checked)}
                >
                  {capitalize(label)}
                </Checkbox>
              </div>
            ),
          })),
        },
        {
          key: "clear",
          label: <span className="text-[#1677FF]">Clear</span>,
          disabled: !hasActiveFilters,
          onClick: clearAllFilters,
        },
      ],
    }),
    [selectedEmailTypes, selectedEmailStatuses, hasActiveFilters]
  );

  return (
    <div className="container h-screen border border-l-0 border-t-0 border-r-0 border-solid border-grey-200">
      <div className="flex-row-centered border-0 border-b border-solid border-grey-200 px-5 py-3 items-center justify-between">
        <Checkbox
          title="Select All"
          onChange={handleSelectAll}
          checked={selectedIds.length > 0 && selectedIds.length === emails.length}
          indeterminate={selectedIds.length > 0 && selectedIds.length < emails.length}
        >
          Select All
        </Checkbox>
        {!isEmpty(selectedIds) && (
          <CustomButton
            disabled={isEmpty(selectedIds)}
            icon={<DeleteIcon color="#334155" size="18" />}
            handleClick={handleMoveToTrash}
            btnText="Move to Trash"
            className="!text-grey-500"
          />
        )}
        {isEmpty(selectedIds) && (
          <Dropdown menu={menuWithCheckboxes} trigger={["click"]}>
            <Button>
              <Space>
                Filter
                {hasActiveFilters &&
                  ` (${selectedEmailTypes.length + selectedEmailStatuses.length})`}
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
        )}
      </div>
      <div className="flex-row-centered gap-1 border-0 border-b border-solid border-grey-200 px-5 py-3 items-center justify-between">
        <DebounceInput
          placeholder="Search lead by name..."
          className="w-full rounded"
          value={params.search}
          allowClear
          onChange={value => setparams({ ...params, search: value, page: 1 })}
        />
        <Popover
          trigger="click"
          content={
            <UserFilter
              value={userList}
              disabled={restrictRemoveDefaultFilter}
              applyUserFilter={async list => {
                setuserList(list);
              }}
              employeeBtnRef={employeeBtnRef}
            />
          }
        >
          <Button
            ref={employeeBtnRef}
            className={`flex-row-centered p-2 ${
              userList?.length > 0 ? "!border-twLinkColor !text-twLinkColor" : "!text-[#64748B]"
            }`}
          >
            <LuUser size={16} />
          </Button>
        </Popover>
      </div>
      <Spin spinning={emailLoader} tip="Loading emails...">
        <div className="overflow-y-auto h-[calc(100vh-300px)]">
          {emails && emails.length > 0 ? (
            emails.map((item, index) => {
              const emailProps = {
                ...props,
                item,
                index,
                selectedIds,
                handleCheckboxChange,
                // Direct list refetch (NOT the parent's registrar that shares
                // the refetchEmailListFn name) so EmailCard's resend can
                // refresh the list.
                refetchEmails: handleRefetchEmails,
              };
              return (
                <div key={index} className="w-full min-h-min">
                  <EmailCard {...emailProps} />
                </div>
              );
            })
          ) : (
            <Empty description="No Emails sent" />
          )}
        </div>
      </Spin>
      <div className="flex justify-center my-4">
        <Pagination
          pageSize={params.pageSize}
          current={params.page}
          total={data?.getEmails?.count || 0}
          showSizeChanger
          size="small"
          showLessItems
          responsive
          onChange={(page, pageSize) => {
            // Check if pageSize is different from current pageSize
            // If it is, onShowSizeChange will handle it
            if (pageSize === params.pageSize) {
              setparams(prev => ({ ...prev, page }));
            }
          }}
          onShowSizeChange={(current, size) => {
            // When page size changes, always reset to page 1
            setparams({ ...params, page: 1, pageSize: size });
          }}
        />
      </div>
    </div>
  );
}
